import ErrorIcon from '@mui/icons-material/Error';
import {
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { ReactComponent as ExpandArrowIcon } from '../../assets/icons/iconExpandArrow.svg';
import { Container } from './styles';
import { Input } from '../Inputs/Input';
import { slugify } from '../../utils/slugify';

export type DropdownPropsItem = {
  id: string | number;
  value: string | number;
};

export interface DropDownListProps {
  errorText?: string;
  elementId?: string;
  messageEmpty?: string;
  isSearchable?: boolean;
  itens: DropdownPropsItem[];
  placeholderTitle?: string;
  onChange: (value: string | number) => void;
  hasBorder?: boolean;
  typeReturn?: 'id' | 'value';
  value?: string | number;
  clearToSelect?: boolean;
  width?: string;
  disabled?: boolean;
  defaultValue?: string;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

export function DropDownList({
  itens,
  elementId,
  placeholderTitle,
  onChange,
  errorText,
  disabled,
  messageEmpty = 'Nenhum item encontrado...',
  isSearchable,
  hasBorder = true,
  typeReturn = 'value',
  value,
  clearToSelect = false,
  width = '100%',
  defaultValue = '',
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: DropDownListProps) {
  const theme = useTheme();
  const [label, setLabel] = useState<string | number>(defaultValue || '');
  const [itensFiltered, setItensFiltered] = useState<DropdownPropsItem[]>(itens);
  const [isOpen, setIsOpen] = useState(false);
  const [showEmptyMessage, setIsShowEmptyMessage] = useState(false);
  const [defaultValues] = useState({
    search: '',
  });
  const { control, watch, setValue } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });
  const search = watch('search');

  const handleScrollFetch = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollTop + clientHeight >= scrollHeight - 200 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  };

  const MenuProps = {
    PaperProps: {
      onScroll: handleScrollFetch,
      style: {
        backgroundColor: theme.colors.background,
        color: theme.colors.secondary,
        maxHeight: '500px',
      },
    },
    disableScrollLock: true,
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    const valueReturn =
      typeReturn === 'value'
        ? event.target.value
        : itens.find((i) => i.id === event.target.value)?.id;

    if (valueReturn !== undefined) onChange(valueReturn);

    if (clearToSelect) {
      setLabel('');
    } else {
      setLabel(itens.find((i) => i.id === event.target.value)?.value!);
    }
  };

  const handleOpen = () => {
    if (!itens.length) {
      setIsShowEmptyMessage(true);
      return;
    }
    setIsOpen(true);
    document.body.classList.add('no-scroll');
  };

  const handleClose = () => {
    setIsOpen(false);
    setValue('search', '');
    document.body.classList.remove('no-scroll');
  };

  const handleScroll = () => {
    setIsOpen(false);
  };

  const handleSearchClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (isOpen) {
      event.stopPropagation();
    }
  };

  useEffect(() => {
    if (!itens) return;
    if (!search) {
      setItensFiltered(itens);
      return;
    }
    setItensFiltered(
      itens.filter((item) => {
        if (item.value) {
          return slugify(item.value.toString()).includes(slugify(search));
        }
        return false;
      }),
    );
  }, [itens, search]);

  useEffect(() => {
    if (itens.length) {
      setIsShowEmptyMessage(false);
    }
  }, [itens.length]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.body.classList.remove('no-scroll');
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (value !== undefined) {
      // Compare with type coercion to handle both string and number ids
      setLabel(itens.find((i) => i.id?.toString() === value?.toString())?.value!);
    }
  }, [itens, value]);

  return (
    <Container hasBorder={hasBorder} widthInput={width} hasError={!!errorText} id={elementId}>
      <Select
        open={isOpen}
        defaultValue={defaultValue}
        onOpen={handleOpen}
        onClose={handleClose}
        disabled={disabled}
        variant="standard"
        value={clearToSelect ? '' : value?.toString()}
        displayEmpty
        onChange={handleChange}
        IconComponent={ExpandArrowIcon}
        MenuProps={MenuProps}
        error={!!errorText}
        onKeyDown={(e) => {
          e.stopPropagation();
          // Desabilita navegação por teclado quando isSearchable está ativo
          if (isSearchable) {
            e.preventDefault();
          }
        }}
        renderValue={() => {
          if (defaultValue) {
            const findedValue = itens.find((i) => i.id?.toString() === defaultValue?.toString());
            return findedValue?.value;
          }
          if (!value && !label) {
            return <span className="placeholder">{placeholderTitle}</span>;
          }
          return label;
        }}>
        {isSearchable && (
          <Grid display="flex" alignItems="center" justifyContent="center" justifyItems="center">
            <Grid width="90%" padding="1rem 0.5rem">
              <Input
                control={control}
                name="search"
                placeholder="Pesquisar..."
                onClick={handleSearchClick}
              />
            </Grid>
          </Grid>
        )}
        <MenuItem value="" disabled style={{ display: 'flex', flexDirection: 'column' }}>
          Selecione
        </MenuItem>
        {itensFiltered.map(({ id, value: valueItem }) => (
          <MenuItem
            key={id + uuid()}
            value={typeReturn === 'id' ? id : valueItem}
            style={{ display: 'flex', flexDirection: 'column' }}
            onKeyDown={(e) => e.stopPropagation()}>
            <Tooltip disableInteractive title={valueItem} arrow>
              <input
                value={valueItem}
                readOnly
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  width: '100%',
                  height: '1.5rem',
                  maxWidth: '80%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: theme.colors.secondary,
                  cursor: 'pointer',
                  outline: 'none',
                  fontSize: '1.1rem',
                  alignSelf: 'center',
                }}
              />
            </Tooltip>
          </MenuItem>
        ))}
        {isFetchingNextPage && (
          <MenuItem disabled style={{ display: 'flex', justifyContent: 'center', height: '3rem' }}>
            <CircularProgress size={30} />
            Carregando...
          </MenuItem>
        )}
      </Select>

      {errorText && (
        <span className="fieldError">
          <ErrorIcon fontSize="inherit" /> {errorText}
        </span>
      )}
      {showEmptyMessage && !errorText && (
        <span className="fieldError">
          <ErrorIcon fontSize="inherit" color="error" /> {messageEmpty}
        </span>
      )}
    </Container>
  );
}
